import React, { MouseEvent, SyntheticEvent, useState, useEffect } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Field, Formik, FormikProps } from 'formik';
import styled, { css } from 'styled-components';
import { Grid, Typography, Colors, Button } from '@inplayer-org/inplayer-ui';

// actions
import {
  loadInitialExternalAssets as loadInitialExternalAssetsAction,
  loadExternalAssets as loadExternalAssetsAction,
  LoadExternalAssetsParams,
} from 'ducks/merchantDucks/items/actions';

// images
import previewImg from 'assets/pics/ip-preview-premium.jpg';

// Types
import { OvpMetadata } from 'pages/MerchantPages/Assets/assetTypes';
import RootState from 'ducks/RootState';
import { ExternalAsset } from 'ducks/merchantDucks/items/types';

// Utils
import JSONParse from 'utils/json-parse-safe';
import truncate from 'lodash/truncate';
import { qbrickThumbnailImage } from 'utils/getQbrickThumbnail';

// components
import Input from 'components/Fields/Input';
import Loader from 'components/Loader';
import ErrorMessage from 'components/ErrorMessage';
import Restricted from 'restrictions/roles/components/Restricted';

// styled-components
import { FullHeightForm, ContentContainer, FullHeightContainer } from 'components/SharedStyledElements';
import StyledContainer from 'components/StyledContainer';
import FormAccordionHelper, { SectionType } from '../../components/AccordionHelper';

const { Container, Cell } = Grid;

interface VideoBoxProps {
  isSelected?: boolean;
}

export const VideoBox = styled(Container)<VideoBoxProps>`
  cursor: pointer;
  border: 1px solid ${Colors.lightGray};
  min-height: 4rem;
  overflow: hidden;
  padding: 0.4rem;
  transition: all 200ms ease-in-out;

  &:hover {
    transform: scale(1.03);
    box-shadow: ${Colors.gray} 0px 0.2rem 0.5rem;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      transform: scale(1.03);
      box-shadow: ${Colors.darkGray} 0px 0.2rem 0.5rem;
      border: 1px solid ${Colors.darkGray};

      &:hover {
        box-shadow: ${Colors.darkGray} 0px 0.2rem 0.5rem;
        border: 1px solid ${Colors.darkGray};
      }
    `}
`;

export const VideoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  vertical-align: middle;
`;

export const TypographyStyled = styled(Typography)`
  font-size: 0.8rem;
`;

const ScrollCell = styled(Cell)`
  padding: 1rem;
  border: 1px solid ${Colors.gray};
  height: 31rem;
  overflow-y: scroll;
  position: relative;
`;

interface FormValues {
  account_id: string;
  client_id: string;
  media_id: string;
}

const defaultValues: FormValues = {
  account_id: '',
  client_id: '',
  media_id: '',
};

interface QbrickVideo {
  asset: { resources: Array<any> };
  thumbnails: Array<any>;
  metadata: { title: string; description: string };
  id: string;
}

interface StateProps {
  externalAccount: {
    keys: { qbrick?: any };
  };
  externalAssets: ExternalAsset;
}

interface DispatchProps {
  loadInitialExternalAssetsNew: (params: LoadExternalAssetsParams) => any;
  loadExternalAssets: (params: LoadExternalAssetsParams) => any;
}

interface OwnProps {
  rawContent: string;
  onSubmit: (contentString: string, externalAssetId: string, metadata?: OvpMetadata) => void;
  formId: string;
  isReadOnly: boolean;
  closePanel: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const QbrickContent = ({
  rawContent,
  onSubmit,
  externalAccount: { keys: { qbrick: { keys: { account_id = 0, client_id = 0 } = {} } = {} } = {} },
  externalAssets: { isFetching, videos, error },
  loadInitialExternalAssetsNew,
  loadExternalAssets,
  formId,
  isReadOnly,
  closePanel,
}: Props) => {
  const initialValues: FormValues = {
    ...defaultValues,
    account_id,
    client_id,
    ...JSONParse(rawContent).value,
  };

  const [metadata, setMetadata] = useState<OvpMetadata | undefined>(undefined);
  const [currentPage, setPage] = useState(0);

  // Handle if fields should be disabled
  const isContentEditingDisabled =
    (account_id && initialValues.account_id !== account_id) || (client_id && client_id !== initialValues.client_id);

  useEffect(() => {
    if (!isFetching) {
      loadInitialExternalAssetsNew({
        type: 'qbrick',
        page: 0,
      });
    }
  }, []);

  const handleSubmit = (data: FormValues) => {
    const contentString = JSON.stringify(data);
    const { media_id: externalAssetId } = data;

    onSubmit(contentString, externalAssetId, metadata);
  };

  const loadMoreVideos = (e: SyntheticEvent) => {
    e.preventDefault();

    loadExternalAssets({
      type: 'qbrick',
      page: currentPage + 1,
      limit: 15,
    });
    setPage(currentPage + 1);
  };

  const handleSelectVideo = (
    videoId: string,
    title: string,
    image: string,
    description: string,
    setFieldValue: (fieldName: string, value: any) => void
  ) => (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    setMetadata({ title, image, description });
    setFieldValue('media_id', videoId);
  };

  const handleManualChange = (setFieldValue: (fieldName: string, value: any) => void) => ({
    target,
  }: SyntheticEvent<HTMLInputElement>) => {
    const { value } = target as HTMLInputElement;

    setMetadata(undefined);
    setFieldValue('media_id', value);
  };

  const renderVideo = (setFieldValue: (fieldName: string, value: any) => void, { media_id }: FormValues) => (
    video: QbrickVideo
  ) => {
    // Default values are set because some of the
    // video attributes are undefined
    const {
      asset: { resources = [] } = { resources: [] },
      thumbnails,
      metadata: { title = '', description = '' } = { title: '', description: '' },
      id,
    } = video;

    const isSelected = media_id === id;

    const thumbnailImage = thumbnails ? qbrickThumbnailImage(resources, thumbnails) : previewImg;

    return (
      <VideoBox
        key={id}
        columns={7}
        onClick={handleSelectVideo(id, title, thumbnailImage, description, setFieldValue)}
        isSelected={isSelected}
      >
        <Cell width={2} center middle>
          <VideoImage alt="thumbnail-img" src={thumbnailImage} />
        </Cell>
        <Cell width={4} left={3} middle>
          <TypographyStyled variant="p">{truncate(title, { length: 15 })}</TypographyStyled>
        </Cell>
      </VideoBox>
    );
  };

  const renderVideoBoxes = (setFieldValue: (fieldName: string, value: any) => void, values: FormValues) => {
    return error ? (
      <ErrorMessage>There was an issue with loading assets from Qbrick. Please try again.</ErrorMessage>
    ) : (
      <Cell>
        <Container rowGap="1rem" columns={1}>
          <Cell>
            <Typography variant="p">Choose your video</Typography>
          </Cell>
          {isFetching ? (
            <Loader position="relative" />
          ) : (
            <>
              <ScrollCell>
                <Container columns={3} gap="2rem 1.5rem">
                  {videos.map(renderVideo(setFieldValue, values))}
                </Container>
              </ScrollCell>
              <Button fullWidth onClick={loadMoreVideos}>
                Load more
              </Button>
            </>
          )}
        </Container>
      </Cell>
    );
  };

  return (
    <FullHeightContainer columns={1} rowGap="2rem">
      <Cell>
        <Formik onSubmit={handleSubmit} initialValues={initialValues}>
          {({ setFieldValue, values }: FormikProps<FormValues>) => (
            <FullHeightForm id={formId}>
              <FormAccordionHelper initialValues={initialValues} index={SectionType.Content} closePanel={closePanel} />
              <ContentContainer withPadding>
                <Container rowGap="2rem" columns={1}>
                  <Cell>
                    <Field
                      tag="text_input_account_id"
                      name="account_id"
                      label="Account ID"
                      component={Input}
                      disabled={!!initialValues.account_id || isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  <Cell>
                    <Field
                      tag="text_input_client_id"
                      name="client_id"
                      label="Client ID"
                      component={Input}
                      disabled={!!initialValues.client_id || isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  <Cell>
                    <Field
                      tag="text_input_media_id"
                      name="media_id"
                      label="Media ID"
                      component={Input}
                      onChange={handleManualChange(setFieldValue)}
                      disabled={isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  {isContentEditingDisabled ? (
                    <StyledContainer margin="1rem 0 0 0" columns={1}>
                      <Cell>
                        <ErrorMessage>
                          You can not change content of this asset, because it is not part of the currently integrated
                          Qbrick account.
                        </ErrorMessage>
                      </Cell>
                    </StyledContainer>
                  ) : (
                    <Restricted isRestricted={isReadOnly}>{renderVideoBoxes(setFieldValue, values)}</Restricted>
                  )}
                </Container>
              </ContentContainer>
            </FullHeightForm>
          )}
        </Formik>
      </Cell>
    </FullHeightContainer>
  );
};

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  externalAccount: state.externalAccount,
  externalAssets: state.items.externalAssets,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  loadInitialExternalAssetsNew: loadInitialExternalAssetsAction,
  loadExternalAssets: loadExternalAssetsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(QbrickContent);
